// Every write the page makes, each through the SDK (which checks, signs and posts it once) and
// told back as a toast. A relayer that does not answer leaves an intent unknown, not failed: the
// account stream settles it.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pt, type Accepted, type DepositPlan, type KeyState, type OpenInput } from "../sdk";
import { declined, describe } from "../lib/errors";
import { walletFor } from "../wallet/signer";
import { accountState, onAccountEvents, useAccount, useOwner } from "./account";
import { recall, remember } from "./intents";
import { symbols } from "./market";
import { showMoment } from "./moments";
import { addPlays, loadRecord, record, streakOf } from "./record";
import { toast } from "./toasts";

function owner(): string {
  const held = accountState.get().owner;
  if (!held) throw new Error("Connect a wallet first");
  return held;
}

function failed(title: string, error: unknown): never {
  if (declined(error)) toast("info", "Cancelled", "Nothing was signed.");
  else toast("error", title, describe(error));
  throw error;
}

function sent(accepted: Accepted, label: string) {
  remember(accepted.intentId, label);
}

/** The session key's state for the connected owner, re-read as the account changes. */
export function useKeyState() {
  const address = useOwner();
  const revision = useAccount()?.sessionKeys.length ?? -1;
  return useQuery({
    queryKey: ["keyState", address, revision],
    queryFn: () => pt.keyState(address as string),
    enabled: address !== null,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useInvalidateKey() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["keyState"] });
}

export async function enableTrading(): Promise<KeyState> {
  try {
    const state = await pt.enable(walletFor(owner()));
    toast("up", "One-tap play is on", "This browser holds a 30-day session key. It can play, never withdraw.");
    return state;
  } catch (error) {
    failed("Could not turn on one-tap play", error);
  }
}

export async function openPosition(input: Omit<OpenInput, "owner">, label: string): Promise<Accepted> {
  try {
    const accepted = await pt.open({ ...input, owner: owner() });
    sent(accepted, label);
    return accepted;
  } catch (error) {
    failed("Call not placed", error);
  }
}

export async function closePositions(ids: string[], label: string): Promise<Accepted[]> {
  try {
    const answers = await pt.close(owner(), ids);
    answers.forEach((accepted) => sent(accepted, label));
    return answers;
  } catch (error) {
    failed("Cash out not sent", error);
  }
}

export async function cancelIntent(intentId: string): Promise<Accepted> {
  try {
    const accepted = await pt.cancel(owner(), intentId);
    toast("info", "Cancel sent");
    return accepted;
  } catch (error) {
    failed("Cancel not sent", error);
  }
}

export async function withdraw(usdc: string): Promise<Accepted> {
  try {
    const accepted = await pt.withdraw(walletFor(owner()), usdc);
    sent(accepted, `Withdraw $${usdc}`);
    toast("info", "Withdrawal sent", `$${usdc} to your HyperCore spot account.`);
    return accepted;
  } catch (error) {
    failed("Withdrawal not sent", error);
  }
}

export async function stake(amount: string): Promise<Accepted> {
  try {
    const accepted = await pt.stake(walletFor(owner()), amount);
    sent(accepted, `Stake ${amount} PAPER`);
    toast("info", "Stake sent", `${amount} PAPER`);
    return accepted;
  } catch (error) {
    failed("Stake not sent", error);
  }
}

export async function unstake(amount: string): Promise<Accepted> {
  try {
    const accepted = await pt.unstake(walletFor(owner()), amount);
    sent(accepted, `Unstake ${amount} PAPER`);
    toast("info", "Unstake sent", `${amount} PAPER`);
    return accepted;
  } catch (error) {
    failed("Unstake not sent", error);
  }
}

export async function claim(): Promise<Accepted> {
  try {
    const accepted = await pt.claim(walletFor(owner()));
    sent(accepted, "Claim your cut");
    toast("info", "Claim sent", "Your cut goes to your bankroll.");
    return accepted;
  } catch (error) {
    failed("Claim not sent", error);
  }
}

export async function planDeposit(usdc: string, source: "spot" | "perp", mode: "standard" | "pooled"): Promise<DepositPlan> {
  try {
    return await pt.planDeposit(owner(), usdc, source, mode);
  } catch (error) {
    failed("Could not plan the deposit", error);
  }
}

export async function sendDeposit(plan: DepositPlan): Promise<void> {
  try {
    await pt.sendDeposit(walletFor(owner(), plan.sends.length));
    toast("up", "Deposit sent", "It lands in your bankroll within a few blocks.");
  } catch (error) {
    failed("Deposit not sent", error);
  }
}

/** Tells how calls ended (the moment and the record) and how sent intents ended: mounted once by
 * the app. */
export function startAccountEvents(): () => void {
  const follow = accountState.subscribe(() => loadRecord(accountState.get().owner));
  loadRecord(accountState.get().owner);
  const stop = onAccountEvents(({ closed, outcomes }) => {
    const owner = accountState.get().owner;
    if (owner && closed.length > 0) {
      const fresh = addPlays(owner, closed, (id) => symbols.get(id) ?? `#${id}`);
      showMoment(fresh, streakOf(record.get().plays));
      if (fresh.length > 0 && "vibrate" in navigator) navigator.vibrate(fresh.some((play) => play.busted) ? [24, 40, 24] : 18);
    }
    for (const [id, outcome] of outcomes) {
      const label = recall(id);
      if (!label) continue;
      if (outcome === "failed") toast("error", `${label} failed`, "The contract refused it.");
      else if (outcome === "expired") toast("error", `${label} expired`, "It was not sent within the hour.");
      else if (outcome === "cancelled") toast("info", `${label} cancelled`);
      else if (outcome === "succeeded" && !label.startsWith("Cash out") && !label.startsWith("Call ")) toast("up", label, "Settled on chain.");
    }
  });
  return () => {
    follow();
    stop();
  };
}
